/**
 * LatheNode - Revolve a 2D profile around the Y axis
 *
 * Takes points defining a profile, revolves to create geometry.
 * Profile points are in the XY plane, revolved around Y axis.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';

const latheEndpoints = {
  ...internals({
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    // Default: simple vase profile - array of [x, y] pairs
    points: {
      dataType: 'array',
      value: [[0.5, 0], [0.8, 0.3], [0.6, 0.8], [0.4, 1.2], [0.3, 1.5], [0.35, 1.8], [0.5, 2]],
      label: 'Profile Points'
    },
    segments: { value: 32, min: 3, max: 128, step: 1 },
    phiStart: { value: 0, min: 0, max: Math.PI * 2, step: 0.01, label: 'Start Angle' },
    phiLength: { value: Math.PI * 2, min: 0, max: Math.PI * 2, step: 0.01, label: 'Arc Length' }
  })
};

class LatheNode extends BaseNode<typeof latheEndpoints> {
  private three: Three;
  /** Stored mesh reference from SceneNode — for in-place geometry replacement */
  private mesh: any = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Build geometry from current params — called by SceneNode */
  buildGeometry(): any {
    return this.generateGeometryResult();
  }

  /** Store mesh reference from SceneNode for in-place param mutation */
  setThreeObject(mesh: any): void {
    this.mesh = mesh;
  }

  /** Per-frame callback for useExp params — rebuilds geometry each frame */
  animateCallback = () => {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  };

  initialize(): void {
    super.initialize();
  }

  private parsePoints(): any[] {
    const arr = this.params.points.value;
    if (!Array.isArray(arr)) {
      throw new Error('LatheNode: points must be an array of [x, y] pairs');
    }
    return arr
      .filter((p: any) => Array.isArray(p) && p.length >= 2)
      .map((p: any) => new this.three.Vector2(Number(p[0]), Number(p[1])));
  }

  private generateGeometryResult(): any {
    const points = this.parsePoints();
    if (points.length < 2) {
      return null;
    }

    const geometry = new this.three.LatheGeometry(
      points,
      this.params.segments.value,
      this.params.phiStart.value,
      this.params.phiLength.value
    );

    geometry.computeVertexNormals();
    return geometry;
  }

  onParamChanged(): void {
    if (this.mesh) {
      const oldGeo = this.mesh.geometry;
      this.mesh.geometry = this.buildGeometry();
      if (oldGeo && typeof oldGeo.dispose === 'function') oldGeo.dispose();
    }
  }
}

export const latheNodeDef: NodeDef<LatheNode> = {
  type: 'Lathe',
  shortName: 'lathe',
  info: 'Revolves a 2D profile around the Y axis to create rotational geometry.',
  category: 'shape',
  endpoints: latheEndpoints,
  ctor: LatheNode
};
